/**
 * ObsSceneDrawer — pick an OBS connection + map worship block types
 * to OBS scenes. Reuses the existing `/api/obs/*` API (encrypted
 * connections, scene switch endpoint). Two scopes:
 *   • per-service override (PUT /admin/worship/services/{id}/obs-config)
 *   • church-wide default   (PUT /admin/worship/obs/global-config)
 *
 * The Presenter Console consumes the *effective* config resolved
 * server-side (service → church → global). Save-service overrides,
 * Save-default writes the church default.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioTower, RefreshCw, Save, Globe2 } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const BLOCK_TYPES = [
  'countdown', 'welcome', 'prayer', 'song', 'scripture',
  'announcement', 'offering', 'sermon', 'ministry_time',
  'communion', 'benediction', 'custom',
];

export default function ObsSceneDrawer({ serviceId, churchId, onClose }) {
  const qc = useQueryClient();
  const [connectionId, setConnectionId] = useState('');
  const [autoSwitch, setAutoSwitch] = useState(true);
  const [sceneMap, setSceneMap] = useState({});
  const [availableScenes, setAvailableScenes] = useState([]);

  const connsQ = useQuery({
    queryKey: ['obs-conns'],
    queryFn: async () => (await axios.get(`${API}/api/obs/connections`, { headers: authH() })).data,
  });

  const effectiveQ = useQuery({
    queryKey: ['obs-effective', serviceId],
    queryFn: async () => (await axios.get(
      `${API}/api/admin/worship/services/${serviceId}/obs-effective`, { headers: authH() },
    )).data,
  });

  // Load effective config into local state on first fetch
  useEffect(() => {
    if (!effectiveQ.data) return;
    const cfg = effectiveQ.data.config || {};
    setConnectionId(cfg.connection_id || '');
    setSceneMap(cfg.scene_map || {});
    setAutoSwitch(cfg.auto_switch !== false);
  }, [effectiveQ.data]);

  // Fetch scenes whenever the connection changes
  useEffect(() => {
    if (!connectionId) { setAvailableScenes([]); return; }
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/obs/connections/${connectionId}/status`, { headers: authH() },
        );
        setAvailableScenes(r.data?.scenes || []);
      } catch (e) {
        setAvailableScenes([]);
      }
    })();
  }, [connectionId]);

  const setScene = (blockType, scene) => {
    setSceneMap((prev) => {
      const next = { ...prev };
      if (!scene) delete next[blockType];
      else next[blockType] = scene;
      return next;
    });
  };

  const saveService = useMutation({
    mutationFn: async () => (await axios.put(
      `${API}/api/admin/worship/services/${serviceId}/obs-config`,
      { connection_id: connectionId || null, scene_map: sceneMap, auto_switch: autoSwitch },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => {
      toast.success('OBS override saved for this service');
      qc.invalidateQueries({ queryKey: ['obs-effective', serviceId] });
    },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  const saveDefault = useMutation({
    mutationFn: async () => (await axios.put(
      `${API}/api/admin/worship/obs/global-config${churchId ? `?church_id=${churchId}` : ''}`,
      { connection_id: connectionId || null, scene_map: sceneMap, auto_switch: autoSwitch },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => toast.success(churchId ? 'Church default saved' : 'Ecosystem default saved'),
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  const testSwitch = async (scene) => {
    if (!connectionId || !scene) return;
    try {
      await axios.post(
        `${API}/api/obs/connections/${connectionId}/scene`,
        { scene_name: scene },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      toast.success(`Switched OBS → ${scene}`);
    } catch (e) {
      toast.error(`OBS: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const origin = effectiveQ.data?.origin;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 max-h-[70vh] overflow-y-auto border-t border-slate-800 bg-slate-950/95 p-3 shadow-2xl" data-testid="obs-drawer">
      <div className="mx-auto max-w-5xl space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-100 flex items-center gap-2">
              <RadioTower className="h-4 w-4 text-rose-300" /> OBS Scene Mapping
            </div>
            <div className="text-[10px] text-slate-500">
              Auto-switches OBS scenes as slides advance.
              {origin && <span className="ml-2">Inheriting from: <span className="text-slate-300">{origin}</span></span>}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="obs-close">Close</Button>
        </div>

        <div className="grid gap-2 md:grid-cols-[1fr_auto_auto]">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Connection</label>
            <select
              data-testid="obs-connection"
              value={connectionId}
              onChange={(e) => setConnectionId(e.target.value)}
              className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs"
            >
              <option value="">— none — auto-switch disabled —</option>
              {(connsQ.data?.connections || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label} · {c.host}:{c.port}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                data-testid="obs-auto"
                checked={autoSwitch}
                onChange={(e) => setAutoSwitch(e.target.checked)}
              />
              Auto-switch on slide change
            </label>
            <Button size="sm" variant="outline"
                    className="border-slate-700 text-slate-200 hover:bg-slate-800"
                    disabled={!connectionId} onClick={async () => {
                      try {
                        const r = await axios.get(`${API}/api/obs/connections/${connectionId}/status`, { headers: authH() });
                        setAvailableScenes(r.data?.scenes || []);
                        toast.success(`${(r.data?.scenes || []).length} scenes found`);
                      } catch (e) {
                        toast.error(`OBS: ${e?.response?.data?.detail || e.message}`);
                      }
                    }}
                    data-testid="obs-refresh">
              <RefreshCw className="mr-1 h-3 w-3" /> Refresh scenes
            </Button>
          </div>
        </div>

        <div>
          <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">
            Block → Scene mapping · {availableScenes.length} scenes available
          </div>
          <div className="grid gap-1 md:grid-cols-2 lg:grid-cols-3">
            {BLOCK_TYPES.map((bt) => (
              <div key={bt} data-testid={`obs-row-${bt}`}
                   className="flex items-center gap-2 rounded border border-slate-800 bg-slate-950 px-2 py-1 text-xs">
                <Badge variant="outline" className="border-slate-700 text-slate-300 text-[10px]">{bt}</Badge>
                <select
                  data-testid={`obs-scene-${bt}`}
                  value={sceneMap[bt] || ''}
                  onChange={(e) => setScene(bt, e.target.value)}
                  className="flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs"
                >
                  <option value="">— none —</option>
                  {availableScenes.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {sceneMap[bt] && (
                  <button type="button" onClick={() => testSwitch(sceneMap[bt])}
                          className="text-[10px] text-emerald-300 hover:underline">
                    test
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <Button size="sm" variant="outline" data-testid="obs-save-default"
                  className="border-slate-700 text-slate-200 hover:bg-slate-800"
                  onClick={() => saveDefault.mutate()}
                  disabled={saveDefault.isPending}>
            <Globe2 className="mr-1 h-3 w-3" />
            {saveDefault.isPending ? 'Saving…' : 'Save as default'}
          </Button>
          <Button size="sm" data-testid="obs-save-service"
                  className="bg-rose-600 hover:bg-rose-500"
                  onClick={() => saveService.mutate()}
                  disabled={saveService.isPending}>
            <Save className="mr-1 h-3 w-3" />
            {saveService.isPending ? 'Saving…' : 'Save for this service'}
          </Button>
        </div>
      </div>
    </div>
  );
}
